/**
 * Stripe Radar session minting for Visa CE 3.0 / Mastercard FPT (PAY-1726, PAY-2001).
 *
 * Card data is collected via VGS Collect and the PaymentIntent is created AND
 * confirmed server-side through the VGS proxy, so the card PaymentIntent is never
 * created through Stripe.js and Stripe's automatic device collection never attaches
 * to it. A Radar session token minted here is the only supported way to link the
 * client's IP and device to a server-created PaymentIntent — Stripe exposes no
 * server-side `customer_ip` / `device_id` parameter.
 *
 * Cards never touch Stripe.js: this only calls `stripe.createRadarSession()`, which
 * snapshots browser/device signals. Collection stays entirely on VGS.
 *
 * One mint per init, started as soon as the key is known. It uses the Stripe.js
 * the merchant loads on the page; PaymentKit.js never adds Stripe.js itself. The
 * Pay click only waits, bounded, for that mint. When the page loads its Stripe.js
 * after the init mint gave up, the Pay click mints then.
 */

import { AnalyticsService } from "../analytics";
import { getLoadedStripe } from "./next-action-handlers";
import { describeRadarSubmit, type RadarMintState, shouldRetryMintAtSubmit } from "./radar-session-decisions";
import { isPageStripeJsAvailable, waitForParseEnd } from "./stripe-js-loader";

/**
 * How long a submit waits for the Radar session, counted from the Pay click.
 *
 * The init-time mint has no cap of its own, so a slow Stripe.js load can still
 * finish while the shopper types. This cap bounds only what is left at Pay (the
 * key wait and the mint wait together). The enrichment is never worth delaying a
 * payment for longer.
 */
export const RADAR_SESSION_SUBMIT_CAP_MS = 2000;

/**
 * The one mint of an init. `settled` resolves once `state` leaves "pending" and
 * never rejects. `startedAt`/`endedAt` are `performance.now()` values.
 */
export type RadarSessionMintHandle = {
  state: RadarMintState;
  key: string | undefined;
  id: string | undefined;
  startedAt: number | undefined;
  endedAt: number | undefined;
  settled: Promise<void>;
};

/**
 * Correlation ids for the `radar_session_timing` PostHog event.
 *
 * Same two ids the `vgs_collect_token_timing` / `vgs_create_card_timing` events carry,
 * so Radar latency joins the rest of the submit timeline per checkout attempt.
 * Optional: without it the submit still waits, it just emits no timing event.
 */
export type RadarSessionAnalyticsContext = {
  /** Checkout secure token — mirrors the `checkout_session_id` on the vgs_*_timing events. */
  checkoutSessionId: string;
  /** Per-attempt request id — mirrors the `request_id` on the vgs_*_timing events. */
  requestId: string;
};

/**
 * Best-effort by design: any failure (Stripe.js blocked by an ad blocker or CSP, a
 * bad publishable key, a network error) resolves to `undefined` so the charge
 * proceeds without the extra dispute-deflection signal rather than failing.
 */
const mintViaStripe = async (publishableKey: string): Promise<string | undefined> => {
  try {
    const stripe = await getLoadedStripe(publishableKey);
    const { radarSession, error } = await stripe.createRadarSession();
    if (error) {
      console.warn("[PaymentKit] Stripe Radar session error:", error.message);
      return undefined;
    }
    // radarSession is typed as Record<any, any> by @stripe/stripe-js; the id is the
    // only field we forward (as radar_options[session] on the PaymentIntent).
    const radarSessionId = typeof radarSession?.id === "string" ? radarSession.id : undefined;
    if (!radarSessionId) console.warn("[PaymentKit] Stripe Radar session returned no id");
    return radarSessionId;
  } catch (err) {
    console.warn("[PaymentKit] Failed to mint Stripe Radar session:", err);
    return undefined;
  }
};

type StripeJsReadiness = "ready" | "absent";

/**
 * Whether the page has a Stripe.js to mint with. Checked at once, then again at the
 * end of parsing, so a merchant tag below PaymentKit.js in the HTML is seen.
 */
const findPageStripeJs = async (): Promise<StripeJsReadiness> => {
  if (isPageStripeJsAvailable()) return "ready";
  await waitForParseEnd();
  return isPageStripeJsAvailable() ? "ready" : "absent";
};

type MintResult = { state: Exclude<RadarMintState, "pending" | "no_key">; id: string | undefined };

const mintWhenReady = async (key: string, readiness: Promise<StripeJsReadiness>): Promise<MintResult> => {
  const ready = await readiness;
  if (ready === "absent") return { state: "no_stripe_js", id: undefined };
  const id = await mintViaStripe(key);
  return { state: id ? "minted" : "failed", id };
};

const recordMint = async (handle: RadarSessionMintHandle, mint: () => Promise<MintResult>): Promise<void> => {
  handle.startedAt = performance.now();
  const result = await mint();
  handle.endedAt = performance.now();
  handle.id = result.id;
  handle.state = result.state;
};

/**
 * Starts the one mint of an init. Returns at once, before the key is known: the
 * handle stays "pending" through the key wait, then the mint. The mint starts the
 * moment the key arrives. `keyReady` must never reject.
 */
export const createRadarSessionMintHandle = (
  keyReady: Promise<string | undefined>,
  isDestroyed: () => boolean,
): RadarSessionMintHandle => {
  const handle: RadarSessionMintHandle = {
    state: "pending",
    key: undefined,
    id: undefined,
    startedAt: undefined,
    endedAt: undefined,
    settled: Promise.resolve(),
  };
  handle.settled = (async () => {
    const key = await keyReady;
    handle.key = key;
    if (!key) {
      handle.state = "no_key";
      return;
    }
    const readiness = isDestroyed() ? Promise.resolve<StripeJsReadiness>("absent") : findPageStripeJs();
    await recordMint(handle, () => mintWhenReady(key, readiness));
  })();
  return handle;
};

/** Mints again, inside the submit cap, when the page's own Stripe.js arrived after the init mint. */
const retryMintIfStripeJsArrived = (handle: RadarSessionMintHandle): void => {
  const key = handle.key;
  if (!key || !shouldRetryMintAtSubmit({ mintState: handle.state, pageStripeJs: isPageStripeJsAvailable() })) {
    return;
  }
  handle.state = "pending";
  handle.settled = recordMint(handle, () => mintWhenReady(key, Promise.resolve("ready")));
};

const emitTiming = (
  analytics: RadarSessionAnalyticsContext | undefined,
  described: ReturnType<typeof describeRadarSubmit>,
  waitMs: number,
  handle: RadarSessionMintHandle | undefined,
): void => {
  if (!analytics) return;
  const mintMs =
    described.mintDurationSet && handle?.startedAt !== undefined && handle.endedAt !== undefined
      ? handle.endedAt - handle.startedAt
      : undefined;
  // AnalyticsService.capture swallows every adapter error, so this never throws.
  AnalyticsService.capture("radar_session_timing", {
    checkout_session_id: analytics.checkoutSessionId,
    request_id: analytics.requestId,
    outcome: described.outcome,
    duration_ms: Math.round(waitMs),
    ready_at_submit: described.readyAtSubmit,
    ...(mintMs !== undefined ? { mint_duration_ms: Math.round(mintMs) } : {}),
  });
};

/**
 * Submit-time wait for the init's Radar session. One cap, started at this call (the
 * Pay click), bounds whatever is left of the mint. Resolves the id to send, or
 * `undefined`. Never rejects and never blocks the payment. Emits one
 * `radar_session_timing` event per call when `analytics` is supplied.
 */
export const waitForRadarSessionAtSubmit = async (
  handle: RadarSessionMintHandle | undefined,
  analytics?: RadarSessionAnalyticsContext,
): Promise<string | undefined> => {
  const clickAt = performance.now();
  if (handle) retryMintIfStripeJsArrived(handle);
  const mintStateAtClick: RadarMintState = handle?.state ?? "no_key";
  if (handle && mintStateAtClick === "pending") {
    let capTimer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<void>((resolve) => {
      capTimer = setTimeout(resolve, RADAR_SESSION_SUBMIT_CAP_MS);
    });
    await Promise.race([handle.settled, cap]);
    clearTimeout(capTimer);
  }
  const described = describeRadarSubmit({
    mintStateAtClick,
    mintStateAtWaitEnd: handle?.state ?? "no_key",
    mintIdAtWaitEnd: handle?.id,
  });
  if (described.outcome === "timeout") {
    console.warn(
      `[PaymentKit] Stripe Radar session not ready ${RADAR_SESSION_SUBMIT_CAP_MS}ms after submit — continuing without it`,
    );
  }
  emitTiming(analytics, described, performance.now() - clickAt, handle);
  return described.sessionId;
};
